import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Printer, ScanLine, Box as BoxIcon } from 'lucide-react';
import { Badge, Button, Container, Empty } from '@easymoving/ui';
import { useBoxes, useItems } from '../../hooks/useUserData';
import { useWorkspace } from '../../context/WorkspaceContext';
import { BoxFormDialog } from '../../components/boxes/BoxFormDialog';
import { destinationTone, statusTone } from '../../lib/taxonomy/tone';
import { HEAVY_BOX_KG, boxWeightKg } from '../../lib/query/measure';
import { useUnits } from '../../lib/units/useUnits';
import { AlertTriangle } from 'lucide-react';

export function BoxesPage() {
  const { t } = useTranslation();
  const { boxes, loading } = useBoxes();
  const { items } = useItems();
  const { canEdit } = useWorkspace();
  const units = useUnits();
  const [creating, setCreating] = useState(false);

  const countByBox = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) if (it.boxId) m.set(it.boxId, (m.get(it.boxId) ?? 0) + 1);
    return m;
  }, [items]);

  if (loading) {
    return (
      <Container className="pt-6">
        <p className="text-graphite py-12">{t('common.loading')}</p>
      </Container>
    );
  }

  return (
    <Container className="pt-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl tracking-snug">{t('boxes.title')}</h1>
        <div className="flex gap-2">
          {boxes.length > 0 && (
            <>
              <Button asChild variant="secondary">
                <Link to="/app/scan">
                  <ScanLine className="size-4" />
                  {t('boxes.scan')}
                </Link>
              </Button>
              <Button asChild variant="secondary">
                <Link to="/app/boxes/labels">
                  <Printer className="size-4" />
                  {t('boxes.printLabels')}
                </Link>
              </Button>
            </>
          )}
          {canEdit && (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              {t('boxes.create')}
            </Button>
          )}
        </div>
      </div>

      {boxes.length === 0 ? (
        <Empty
          title={t('boxes.empty')}
          body={t('boxes.emptyBody')}
          action={
            canEdit ? (
              <Button size="lg" onClick={() => setCreating(true)}>
                <Plus className="size-5" />
                {t('boxes.create')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {boxes.map((box) => {
            const kg = boxWeightKg(items, box.id);
            return (
              <Link key={box.id} to={`/app/boxes/${box.id}`} className="surface p-4 flex flex-col gap-2 hover:elev-pop transition-shadow">
                <div className="flex items-center gap-2">
                  <BoxIcon className="size-5 text-graphite-soft" />
                  <span className="font-medium text-[16px]">{box.name}</span>
                </div>
                <span className="text-[13px] text-graphite-soft">
                  {t('boxes.itemCount', { count: countByBox.get(box.id) ?? 0 })}
                  {kg > 0 && <> · {units.formatWeight(kg)}</>}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {box.destination && (
                    <Badge tone={destinationTone(box.destination)}>{t(`enum.destination.${box.destination}`)}</Badge>
                  )}
                  {box.progressStatus && (
                    <Badge tone={statusTone(box.progressStatus)}>{t(`enum.status.${box.progressStatus}`)}</Badge>
                  )}
                  {kg > HEAVY_BOX_KG && (
                    <Badge tone="amber" data-testid="heavy-box">
                      <AlertTriangle className="size-3" />
                      {t('boxes.tooHeavy')}
                    </Badge>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <BoxFormDialog open={creating} onOpenChange={setCreating} />
    </Container>
  );
}
